import { Component, Input, OnInit, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { QaiTableComponent, type HeaderConfig, type TableRow, QaiSearchableSelectComponent, QaiSearchableSelectOption, QaiLoadingComponent, QaiIconComponent, QaiDatepickerComponent } from '@qai/ui';
import { TranslationService } from 'src/app/services/translation.service';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { CsvExportService } from 'src/app/services/general/csv-export.service';
import { formatDurationSeconds } from 'src/app/utils/date-parser.util';
import { IAssignmentViewStatsResponse } from 'src/app/interfaces/reportingV2/iassignment-view-stats-response';
import { ActivatedRoute } from '@angular/router';
import { ReportingDashboardService } from 'src/app/services/reporting-dashboard/reporting-dashboard.service';
import { ReportingLocalStorageUtil } from 'src/app/utils/reporting-localstorage.util';
import { IAssignmentViewStatsRequest } from 'src/app/interfaces/reportingV2/iassignment-view-stats-request';
import { LocaleService } from 'src/app/services/locale.service';
import { AmplitudeService } from 'src/app/services/external-tools/amplitude.service';
import { AmplitudeTrackingEvents } from 'src/app/utils/enums/amplitude/amplitudeTrackingEvents.enum';
import { Router } from '@angular/router';
import { getLocalStorageRoleNames, isTeamManager } from 'src/app/utils/local-storage.util';
import { AppConstants } from 'src/app/app-constants';
import { withDemoModule } from 'src/app/utils/demo-mode/demo-mode-context.util';

@Component({
  selector: 'app-practice-sessions',
  templateUrl: './practice-sessions.component.html',
  styleUrls: ['./practice-sessions.component.scss'],
  standalone: true,
  imports: [CommonModule, QaiTableComponent, QaiSearchableSelectComponent, TranslocoModule, QaiLoadingComponent, QaiIconComponent, QaiDatepickerComponent],
})
export class PracticeSessionsComponent implements OnInit {
  @Input() selectedTeams: string[] = [];
  isTranslationLoaded = false;
  headers: HeaderConfig[] = [];
  isLoading = false;
  practiceId: string | null = null;
  practiceSessions: IAssignmentViewStatsResponse[] = [];

  // Original data
  originalData: TableRow[] = [];
  // Filtered data for display
  tableData: TableRow[] = [];
  
  // Filter options
  userOptions: QaiSearchableSelectOption[] = [];
  simulationOptions: QaiSearchableSelectOption[] = [];

  // Selected filter values - now arrays for multiselect
  selectedUsers: string[] | string = [];
  selectedSimulations: string[] | string = [];
  userClearOption = { enable: true, label: 'Clear' };
  simulationClearOption = { enable: true, label: 'Clear' };

  accountUuid: string | null = null;

  defaultSortColumn = "";
  defaultSortDirection: 'desc' = 'desc';
  pageSize = 10;
  currentPage = 1;
  showPagination = true;

  startDate: string | null = null;
  endDate: string | null = null;
  minStartDate: string = '';
  maxStartDate: string = '';
  minEndDate: string = '';
  maxEndDate: string = '';

  userRoles: string[] = getLocalStorageRoleNames();
  enablementAdmin: boolean = this.userRoles.includes("EnablementAdmin");

  simulationNameColumnHeader = this.translocoService.translate('SIMULATION', {}, 'reportingV2');

  @Input() moduleAssignmentUuid: string = '';

  @Input() assignmentName: string = '';

  scopedOptionsSelectionModeUsers = {
    enable: true,
    selectAllOption: { value: 'selectAll', label: 'Select all' },
    deselectAllOption: { value: 'deselectAll', label: 'Deselect all' },
  };
  scopedOptionsSelectionModeSimulations = {
    enable: true,
    selectAllOption: { value: 'selectAll', label: 'Select all' },
    deselectAllOption: { value: 'deselectAll', label: 'Deselect all' },
  };

  constructor(
    private translationService: TranslationService,
    private translocoService: TranslocoService,
    private csvExportService: CsvExportService,
    private route: ActivatedRoute,
    private reportingDashboardService: ReportingDashboardService,
    private amplitudeService: AmplitudeService,
    private localeService: LocaleService,
    private router: Router
  ) { }

  ngOnInit() {
    this.practiceId = this.route.snapshot.paramMap.get('id');
    this.accountUuid = ReportingLocalStorageUtil.getStoredAccountUuid();
    this.translationService.loadScopedTranslation('reportingV2').then(() => {
      this.isTranslationLoaded = true;
      this.scopedOptionsSelectionModeUsers.selectAllOption!.label =
        this.translocoService.translate('SELECT_ALL', {}, 'reportingV2');
      this.scopedOptionsSelectionModeUsers.deselectAllOption!.label =
        this.translocoService.translate('DESELECT_ALL', {}, 'reportingV2');
      this.scopedOptionsSelectionModeSimulations.selectAllOption!.label =
        this.translocoService.translate('SELECT_ALL', {}, 'reportingV2');
      this.scopedOptionsSelectionModeSimulations.deselectAllOption!.label =
        this.translocoService.translate('DESELECT_ALL', {}, 'reportingV2');
      this.userClearOption.label = this.translocoService.translate('CLEAR', {}, 'reportingV2');
      this.simulationClearOption.label = this.translocoService.translate('CLEAR', {}, 'reportingV2');
      this.initializeHeaders();
      this.defaultSortColumn = this.headers[2].name; 
      this.getPracticeSessions();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedTeams'] && this.isTranslationLoaded) {
      this.getPracticeSessions();
    }
  }

  private initializeHeaders() {
    this.headers = [
      {
        name: this.translocoService.translate('USER_NAME', {}, 'reportingV2'),
        sortable: true,
        columnWidth: '10%',
        clickable: true
      },
      {
        name: this.simulationNameColumnHeader,
        sortable: true,
        columnWidth: '15%',
        clickable: true
      },
      {
        name: this.translocoService.translate('SESSION_DATE', {}, 'reportingV2'),
        sortable: true,
        columnWidth: '10%',
      },
      {
        name: this.translocoService.translate('DURATION_OF_SESSION', {}, 'reportingV2'),
        sortable: true,
        columnWidth: '14%',
      },
      {
        name: this.translocoService.translate('OVERALL_SCORE', {}, 'reportingV2'),
        sortable: true,
        columnWidth: '10%',
      },
      {
        name: this.translocoService.translate('MESSAGING_SCORE_LABEL', {}, 'reportingV2'),
        sortable: true,
        columnWidth: '15%'
      },
      {
        name: this.translocoService.translate('METHODOLOGY_SCORE', {}, 'reportingV2'),
        sortable: true,
        columnWidth: '13%'
      },
      {
        name: this.translocoService.translate('SKILLS_SCORE', {}, 'reportingV2'),
        sortable: true,
        columnWidth: '13%'
      }
    ];
  }

  private transformApiDataToTableRows(
    apiData: IAssignmentViewStatsResponse[]
  ): TableRow[] {
    return apiData.map((attempt) => this.createAttemptTableRow(attempt));
  }

  private createAttemptTableRow(attempt: IAssignmentViewStatsResponse): TableRow {
    const row = {
      id: attempt.userUuid,
      email: attempt.userEmail,
      teams: attempt.userTeams,
      uuid: attempt.uuid,
      simulationUuid: attempt.simulationUuid,
      [this.headers[0].name]: { type: 'text', value: attempt.userName },
      [this.headers[1].name]: { type: 'text', value: attempt.simulationName },
      [this.headers[2].name]: { type: 'text', value: this.localeService.formatSessionDateTime(attempt.sessionDate) },
      [this.headers[3].name]: {
        type: 'text',
        value: this.translateDuration(formatDurationSeconds(attempt.durationSeconds)),
      },
      [this.headers[4].name]: {
        type: 'text',
        value: this.localeService.formatScoreValue(attempt.overallScore?.toString()),
      },
      [this.headers[5].name]: { type: 'text', value: this.localeService.formatScoreValue(attempt.messagingScore) },
      [this.headers[6].name]: { type: 'text', value: this.localeService.formatScoreValue(attempt.methodologyScore) },
      [this.headers[7].name]: { type: 'text', value: this.localeService.formatScoreValue(attempt.skillsScore) },
    } as const;

    return { ...row } as unknown as TableRow;
  }

  onDateChange(controlName: 'startDate' | 'endDate', value: string | null): void {
    this.amplitudeService.trackEvent(AmplitudeTrackingEvents.REPORTINGDASHBOARD_CLICK_DATE_FILTER);
    const dateValue = value ?? '';

    if (controlName === 'startDate') {
      this.startDate = dateValue;
      this.handleStartDateChange(dateValue);
    }

    if (controlName === 'endDate') {
      this.endDate = dateValue;
      this.validateDateRange();
    }

    this.applyFilters();
  }

  private handleStartDateChange(startDateValue: string): void {
    if (startDateValue) {
      this.minEndDate = startDateValue;

      if (this.endDate) {
        this.validateDateRange();
      }
    } else {
      this.minEndDate = this.minStartDate;
    }
  }

  private validateDateRange(): void {
    if (this.startDate && this.endDate) {
      const start = new Date(this.startDate);
      start.setHours(0, 0, 0, 0);

      const end = new Date(this.endDate);
      end.setHours(23, 59, 59, 999);

      if (end < start) {
        console.warn('Invalid date range: End date is before start date');
        this.endDate = '';
      }
    }
  }

  private initializeFilters() {
    // Extract unique user names - only from text cells
    const uniqueUsers = [...new Set(
      this.originalData
        .map(row => row[this.headers[0].name])
        .filter(cell => cell.type === 'text')
        .map(cell => (cell as any).value)
    )];
    this.userOptions = uniqueUsers.map(user => ({
      label: user,
      value: user
    }));

    // Extract unique simulations - only from text cells
    const uniqueSimulations = [...new Set(
      this.originalData
        .map(row => row[this.headers[1].name])
        .filter(cell => cell.type === 'text')
        .map(cell => (cell as any).value)
    )];
    this.simulationOptions = uniqueSimulations.map(simulation => ({
      label: simulation,
      value: simulation
    }));

    this.initializeDateRange();
  }

  private initializeDateRange(): void {
    const dateCells = this.originalData
      .map(row => {
        const cell = row[this.headers[2].name];
        return (cell as any)?.value;
      })
      .filter(dateStr => !!dateStr)
      .map(dateStr => new Date(dateStr))
      .filter(d => !isNaN(d.getTime()));

    if (!dateCells.length) return;

    const minDate = new Date(Math.min(...dateCells.map(d => d.getTime())));
    const maxDate = new Date(Math.max(...dateCells.map(d => d.getTime())));

    const toLocalDateString = (date: Date): string => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    this.minStartDate = toLocalDateString(minDate);
    this.maxStartDate = toLocalDateString(maxDate);
    this.minEndDate = toLocalDateString(minDate);
    this.maxEndDate = toLocalDateString(maxDate);
  }

  onUserFilterChange(selectedValue: string | string[]) {
    this.amplitudeService.trackEvent(AmplitudeTrackingEvents.REPORTINGDASHBOARD_CLICK_USER_NAME_FILTER);
    // Handle multiselect - always expect an array
    this.selectedUsers = Array.isArray(selectedValue) ? selectedValue : [selectedValue];
    this.applyFilters();
  }

  onSimulationFilterChange(selectedValue: string | string[]) {
    // Handle multiselect - always expect an array
    this.selectedSimulations = Array.isArray(selectedValue) ? selectedValue : [selectedValue];
    this.applyFilters();
  }

  onSimulationSelected(simulationName: string) {
    this.amplitudeService.trackEvent(
      AmplitudeTrackingEvents.REPORTINGDASHBOARD_CLICK_SIMULATION_FILTER,
      { simulation_name: simulationName }
    );
  }

  private applyFilters() {
    let filteredData = [...this.originalData];

    // Apply user filter - check if any selected user matches
    if (this.selectedUsers.length > 0) {
      filteredData = filteredData.filter(row => {
        const cell = row[this.headers[0].name];
        if (cell.type === 'text') {
          return this.selectedUsers.includes((cell as any).value);
        }
        return false;
      });
    }

    // Apply simulation filter - check if any selected simulation matches
    if (this.selectedSimulations.length > 0) {
      filteredData = filteredData.filter(row => {
        const cell = row[this.headers[1].name];
        if (cell.type === 'text') {
          return this.selectedSimulations.includes((cell as any).value);
        }
        return false;
      });
    }

    if (this.startDate || this.endDate) {
      filteredData = filteredData.filter((row) => {
        const cell = row[this.headers[2].name];
        const rawDate = (cell as any)?.value;
        const rowDate = new Date(rawDate);

        if (isNaN(rowDate.getTime())) return false;

        const rowOnly = new Date(rowDate);
        rowOnly.setHours(0, 0, 0, 0);

        const start = this.startDate ? new Date(this.startDate) : null;
        if (start) start.setHours(0, 0, 0, 0);

        const end = this.endDate ? new Date(this.endDate) : null;
        if (end) end.setHours(23, 59, 59, 999);

        return (
          (!start || rowOnly >= start) &&
          (!end || rowOnly <= end)
        );
      });
    }

    this.tableData = filteredData;
  }

  pageChanged(event: { page: number }): void {
    this.currentPage = event.page;
  }

  clearFilters() {
    this.selectedUsers = [];
    this.selectedSimulations = [];
    this.applyFilters();
  }

  protected onDownloadClick(): void {
    const fileName = this.csvExportService.getCsvFileName(this.assignmentName, 'session_data');
    const sessionIdHeader = this.translocoService.translate('SESSION_ID', {}, 'reportingV2');
    const nameHeader = this.headers[0]?.name;
    const emailHeader = this.translocoService.translate('EMAIL_LABEL', {}, 'reportingV2');
    const teamsHeader = this.translocoService.translate('TEAM_LABEL', {}, 'reportingV2');
    const uiSimulationHeader = this.headers[1]?.name;
    const csvSimulationHeader = this.translocoService.translate('SIMULATION_NAME', {}, 'reportingV2');

    const csvData = this.originalData.map(row => {
      const csvRow: Record<string, unknown> = {
        [sessionIdHeader]: row.id,
        [nameHeader]: row[nameHeader],
        [emailHeader]: row['email'],
        [teamsHeader]: row['teams']
      };

      this.headers.forEach(header => {
        if (header.name === nameHeader) return;
        if (header.name === uiSimulationHeader) {
          csvRow[csvSimulationHeader] = row[header.name];
          return;
        }
        csvRow[header.name] = row[header.name];
      });

      return csvRow;
    });

    const headerNames = [
      sessionIdHeader,
      nameHeader,
      emailHeader,
      teamsHeader,
      ...this.headers.map(h =>
        h.name === uiSimulationHeader ? csvSimulationHeader : h.name
      )
      .filter(n => n !== nameHeader)
    ];

    this.csvExportService.exportToCsv(csvData, fileName, headerNames);
  }

  private translateDuration(durationString: string): string {
    const minText = this.translocoService.translate('MIN', {}, 'reportingV2');
    const secText = this.translocoService.translate('SEC', {}, 'reportingV2');
    return durationString.replace('min', minText).replace('sec', secText);
  }

  private getPracticeSessions(): void {
    const accountUuid = this.accountUuid;
    const moduleAssignmentUuid = this.practiceId;
    const teamUuids: string[] = this.selectedTeams.includes('all') ? [] : this.selectedTeams;
    this.isLoading = true;

    if (!accountUuid || !moduleAssignmentUuid) {
      console.warn('Missing required data for practice sessions', accountUuid, moduleAssignmentUuid);
      this.isLoading = false;
      return;
    }

    const payload: IAssignmentViewStatsRequest = {
      accountUuid: accountUuid,
      teamUuids: teamUuids,
      moduleAssignmentUuid: moduleAssignmentUuid,
    };

    this.reportingDashboardService.getAssignmentViewStats(payload, withDemoModule(AppConstants.NEW_REPORTING_UI)).subscribe({
      next: (practiceSessions) => {
        this.practiceSessions = practiceSessions;
        this.originalData = this.transformApiDataToTableRows(practiceSessions);
        this.tableData = [...this.originalData];
        this.initializeFilters();
        this.applyFilters();
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading practice sessions:', error);
        this.practiceSessions = [];
        this.originalData = [];
        this.tableData = [];
        this.isLoading = false;
      }
    });
  }

  onCellClick(event: { rowData: TableRow; columnName: string; event: Event }) {
    if(event.columnName === this.simulationNameColumnHeader) {
      const simulation = event.rowData[this.simulationNameColumnHeader] as { type: 'text'; value: string };
      this.amplitudeService.trackEvent(AmplitudeTrackingEvents.ASSIGNMENTREPORT_CLICK_SESSIONTABLE_SIMNAME, {
      simulation_name: String(simulation.value),
      });
      this.navigateToSimulationDetail(String(event.rowData['simulationUuid']), simulation.value);
    }
    else
      this.navigateToUserDetail(event.rowData.id);
  }

  navigateToUserDetail(userId: string | number) {
    this.router.navigate(['/reporting_dashboard/user', userId], {
      queryParams: { assignmentUuid: this.moduleAssignmentUuid },
    });
  }

  navigateToSimulationDetail(simulationId: string | number, simulationName: string) {
    this.router.navigate(['/reporting_dashboard/simulation', simulationId], {
      state: {
        simulationName: simulationName,
        assignmentName: this.assignmentName,
        selectedTeamUuids: this.selectedTeams ?? [],
      },
      queryParams: { moduleAssignmentUuid: this.moduleAssignmentUuid },
    });
  }
}